/**
 * Brand contact info, fetched once from the server's `/api/config`
 * endpoint. The server reads `HIVE_*` env vars and returns plain JSON;
 * we never reference `process.env` in client code (it isn't defined in
 * the browser bundle when using `Bun.serve()` programmatically).
 *
 * Anything left unset on the server renders as a clean em-dash. We
 * never fabricate phone numbers, addresses, or RERA numbers.
 */

import { useEffect, useState } from "react";

export interface ConfigField {
  /** Raw value, present only when configured. */
  raw: string | undefined;
  /** Display string — the value when configured, else `—`. */
  display: string;
  /** True when configured (non-empty). */
  set: boolean;
}

export interface SiteConfig {
  phone: ConfigField;
  whatsapp: ConfigField;
  email: ConfigField;
  addressLine1: ConfigField;
  addressLine2: ConfigField;
  hours: ConfigField;
  reraOrn: ConfigField;
  social: {
    instagram: ConfigField;
    facebook: ConfigField;
    linkedin: ConfigField;
    tiktok: ConfigField;
    youtube: ConfigField;
  };
}

/** Server response shape: each field is `string | null` (no wrapping). */
interface RawSiteConfig {
  phone: string | null;
  whatsapp: string | null;
  email: string | null;
  addressLine1: string | null;
  addressLine2: string | null;
  hours: string | null;
  reraOrn: string | null;
  instagram: string | null;
  facebook: string | null;
  linkedin: string | null;
  tiktok: string | null;
  youtube: string | null;
}

function field(value: string | null | undefined): ConfigField {
  const trimmed = value?.trim();
  return {
    raw: trimmed || undefined,
    display: trimmed || "-",
    set: Boolean(trimmed),
  };
}

// Sensible client-side defaults that match the server's defaults in
// `src/index.ts` / `api/config.ts`. We ship these into the initial
// React state so the first paint is never an empty "Talk to Us" column,
// missing floating WhatsApp button, or blank contact page. Server may
// override any of these via `HIVE_*` env vars and the actual response
// from `/api/config` always wins once it arrives.
const DEFAULTS: SiteConfig = {
  phone: field("+971 4 576 2761"),
  whatsapp: field("+971 52 514 1975"),
  email: field("info@hivehomes.ae"),
  addressLine1: field("Dubai, Jumeirah Village Circle"),
  addressLine2: field("Binghatti Emerald, Office 213"),
  hours: field("Monday to Friday, 10am – 7pm"),
  reraOrn: field("42280"),
  social: {
    instagram: field("https://www.instagram.com/hivehomes.ae"),
    facebook: field(
      "https://www.facebook.com/profile.php?id=61560474715761",
    ),
    linkedin: field("https://www.linkedin.com/company/hivehomesae"),
    tiktok: field("https://www.tiktok.com/@hivehomesrealestate"),
    youtube: field("https://www.youtube.com/@HiveHomesRealEstate"),
  },
};

let cached: SiteConfig | null = null;
let inflight: Promise<SiteConfig> | null = null;

function fetchConfig(): Promise<SiteConfig> {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = fetch("/api/config")
    .then((r) => (r.ok ? (r.json() as Promise<RawSiteConfig>) : Promise.reject(new Error(`HTTP ${r.status}`))))
    .then((raw): SiteConfig => {
      const cfg: SiteConfig = {
        phone: field(raw.phone),
        whatsapp: field(raw.whatsapp),
        email: field(raw.email),
        addressLine1: field(raw.addressLine1),
        addressLine2: field(raw.addressLine2),
        hours: field(raw.hours),
        reraOrn: field(raw.reraOrn),
        social: {
          instagram: field(raw.instagram),
          facebook: field(raw.facebook),
          linkedin: field(raw.linkedin),
          tiktok: field(raw.tiktok),
          youtube: field(raw.youtube),
        },
      };
      cached = cfg;
      return cfg;
    })
    .catch(() => DEFAULTS)
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Hook that returns the brand config. First render returns the empty
 *  config (everything renders as `—`); once the server response arrives
 *  (~50 ms) the component re-renders with real values. */
export function useSiteConfig(): SiteConfig {
  const [config, setConfig] = useState<SiteConfig>(cached ?? DEFAULTS);
  useEffect(() => {
    if (cached) return; // already loaded, nothing to do
    let cancelled = false;
    fetchConfig().then((c) => {
      if (!cancelled) setConfig(c);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  return config;
}

/* ---------- href helpers ---------- */

export function telHref(f: ConfigField): string {
  return f.set ? `tel:${f.raw!.replace(/\s+/g, "")}` : "#";
}

export function mailHref(f: ConfigField): string {
  return f.set ? `mailto:${f.raw!}` : "#";
}

export function whatsappHref(f: ConfigField): string {
  if (!f.set) return "#";
  return `https://wa.me/${f.raw!.replace(/[^0-9]/g, "")}`;
}
